import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MessageSquarePlus, MessageSquare, PanelLeftClose, PanelLeftOpen, Trash2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@/lib/utils';
import { Logo, LogoIcon } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

const CHAT_STORAGE_KEY = 'backr-chat-sessions-v1';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: number;
}

export interface ChatSession {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
}

export interface ChatLayoutContext {
  chats: ChatSession[];
  createChat: (prompt: string) => string;
  sendMessage: (chatId: string, prompt: string) => void;
  loadingChatId: string | null;
}

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? '';
};

async function callTitle(message: string): Promise<string> {
  try {
    const res = await fetch('/api/chat/title', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getToken()}` },
      body: JSON.stringify({ message }),
    });
    if (!res.ok) return message.slice(0, 52);
    const data = await res.json();
    return data.title || message.slice(0, 52);
  } catch {
    return message.slice(0, 52);
  }
}

async function callAI(message: string): Promise<string> {
  const res = await fetch('/api/chat/ai', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`,
    },
    body: JSON.stringify({ message }),
  });
  if (res.status === 401) {
    return 'Your session expired. Please sign in again.';
  }
  if (res.status === 403) {
    return "I'm here to help with startup fundraising and investor discovery on RaiseBeacon. What can I help you with today?";
  }
  if (!res.ok) throw new Error('AI request failed');
  const data = await res.json();
  return data.reply as string;
}

const createId = () => {
  const random = Math.random().toString(36).slice(2, 8);
  return `${Date.now().toString(36)}-${random}`;
};

const getTitleFromPrompt = (prompt: string) => {
  const cleaned = prompt.replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'Untitled chat';
  return cleaned.slice(0, 52);
};


const readStoredChats = (): ChatSession[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatSession[];
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
};

export function ChatMain() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chats, setChats] = useState<ChatSession[]>(() => readStoredChats());
  const [loadingChatId, setLoadingChatId] = useState<string | null>(null);

  useEffect(() => {
    window.localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chats));
  }, [chats]);

  const createChat = (prompt: string) => {
    const trimmed = prompt.trim();
    const now = Date.now();
    const chatId = createId();

    const userMessage: ChatMessage = {
      id: createId(),
      role: 'user',
      content: trimmed,
      createdAt: now,
    };

    const newChat: ChatSession = {
      id: chatId,
      title: getTitleFromPrompt(trimmed),
      createdAt: now,
      updatedAt: now,
      messages: [userMessage],
    };

    setChats((prev) => [newChat, ...prev]);
    setLoadingChatId(chatId);

    callTitle(trimmed).then((title) => {
      setChats((prev: ChatSession[]) =>
        prev.map((chat: ChatSession) => chat.id !== chatId ? chat : { ...chat, title })
      );
    });

    callAI(trimmed).then((reply) => {
      setChats((prev: ChatSession[]) =>
        prev.map((chat: ChatSession) =>
          chat.id !== chatId ? chat : {
            ...chat,
            updatedAt: Date.now(),
            messages: [...chat.messages, { id: createId(), role: 'assistant' as const, content: reply, createdAt: Date.now() }],
          }
        )
      );
    }).catch(() => {
      setChats((prev: ChatSession[]) =>
        prev.map((chat: ChatSession) =>
          chat.id !== chatId ? chat : {
            ...chat,
            messages: [...chat.messages, { id: createId(), role: 'assistant' as const, content: 'Something went wrong. Please try again.', createdAt: Date.now() }],
          }
        )
      );
    }).finally(() => setLoadingChatId(null));

    return chatId;
  };

  const sendMessage = (chatId: string, prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed) return;

    const userMessage: ChatMessage = {
      id: createId(),
      role: 'user',
      content: trimmed,
      createdAt: Date.now(),
    };

    setChats((prev) =>
      prev.map((chat) =>
        chat.id !== chatId ? chat : {
          ...chat,
          title: chat.messages.length === 0 ? getTitleFromPrompt(trimmed) : chat.title,
          updatedAt: Date.now(),
          messages: [...chat.messages, userMessage],
        }
      )
    );
    setLoadingChatId(chatId);

    callAI(trimmed).then((reply) => {
      setChats((prev) =>
        prev.map((chat) =>
          chat.id !== chatId ? chat : {
            ...chat,
            updatedAt: Date.now(),
            messages: [...chat.messages, { id: createId(), role: 'assistant', content: reply, createdAt: Date.now() }],
          }
        )
      );
    }).catch(() => {
      setChats((prev) =>
        prev.map((chat) =>
          chat.id !== chatId ? chat : {
            ...chat,
            messages: [...chat.messages, { id: createId(), role: 'assistant', content: 'Something went wrong. Please try again.', createdAt: Date.now() }],
          }
        )
      );
    }).finally(() => setLoadingChatId(null));
  };

  const deleteChat = (chatId: string) => {
    setChats((prev: ChatSession[]) => prev.filter((c: ChatSession) => c.id !== chatId));
    if (location.pathname === `/chat/${chatId}`) navigate('/chat/new');
  };

  const sortedChats = useMemo(
    () => [...chats].sort((a, b) => b.updatedAt - a.updatedAt),
    [chats]
  );

  const getUserInitials = () => {
    if (user?.name) {
      return user.name
        .split(' ')
        .map((namePart: string) => namePart[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <div className="chat-ui h-screen overflow-hidden bg-background">
      <aside
        style={{ width: sidebarCollapsed ? 84 : 280, transition: 'width 0.25s ease' }}
        className={cn(
          'fixed left-0 top-0 z-50 h-screen border-r border-white/15 bg-gradient-to-b from-[#2B7FFF] via-[#1F6DE5] to-[#174FB0] text-white',
          'flex flex-col shadow-xl shadow-black/5'
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-white/15 bg-black/10 px-4">
          {!sidebarCollapsed ? (
            <NavLink to="/chat/new" className="flex items-center gap-3 transition-opacity hover:opacity-90">
              <Logo size="sm" textClassName="fill-white" />
            </NavLink>
          ) : (
            <NavLink to="/chat/new" className="mx-auto transition-opacity hover:opacity-90">
              <LogoIcon size={36} />
            </NavLink>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={cn('h-8 w-8 rounded-lg text-white/80 hover:bg-white/15 hover:text-white', sidebarCollapsed && 'mx-auto')}
          >
            {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>

        <div className="px-3 pb-2 pt-4">
          <NavLink
            to="/chat/new"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition',
                sidebarCollapsed && 'justify-center px-0',
                isActive
                  ? 'bg-white text-[#1C5CCC]'
                  : 'bg-white/10 text-white hover:bg-white/20'
              )
            }
          >
            <MessageSquarePlus className="h-4 w-4" />
            {!sidebarCollapsed && <span>New Chat</span>}
          </NavLink>
        </div>

        <ScrollArea className="flex-1 px-2 pb-3">
          <nav className="space-y-1">
            <AnimatePresence initial={false} mode="popLayout">
              {sortedChats.map((chat) => {
                const isActive = location.pathname === `/chat/${chat.id}`;
                return (
                  <motion.div
                    key={chat.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className={cn(
                      'flex items-start gap-1 rounded-xl transition',
                      isActive ? 'bg-white' : 'hover:bg-white/12'
                    )}
                  >
                    <NavLink
                      to={`/chat/${chat.id}`}
                      className={cn(
                        'flex min-w-0 flex-1 items-start gap-2 px-3 py-2 text-xs',
                        sidebarCollapsed && 'justify-center px-0',
                        isActive ? 'text-[#1C5CCC]' : 'text-white/85 hover:text-white'
                      )}
                      title={chat.title}
                    >
                      <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {!sidebarCollapsed && <span className="break-words leading-snug">{chat.title}</span>}
                    </NavLink>
                    {!sidebarCollapsed && (
                      <button
                        type="button"
                        onClick={() => deleteChat(chat.id)}
                        className={cn(
                          'self-center shrink-0 rounded-md p-1.5 mr-1 transition-opacity',
                          isActive
                            ? 'text-[#1C5CCC] opacity-70 hover:opacity-100'
                            : 'text-white/80 opacity-70 hover:opacity-100 hover:bg-white/20'
                        )}
                        title="Delete chat"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {sortedChats.length === 0 && !sidebarCollapsed && (
              <p className="px-3 py-2 text-xs text-white/70">No chats yet. Start one from New Chat.</p>
            )}
          </nav>
        </ScrollArea>

        {!sidebarCollapsed && user && (
          <div className="border-t border-white/15 bg-black/10 p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9 ring-2 ring-white/25">
                <AvatarFallback className="bg-white text-[#1C5CCC] text-sm font-semibold">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-white">{user?.name || user?.email}</p>
                <p className="truncate text-xs text-white/70">Chat workspace</p>
              </div>
            </div>
          </div>
        )}
      </aside>

      <main
        style={{ marginLeft: sidebarCollapsed ? 84 : 280, transition: 'margin-left 0.25s ease' }}
        className="flex h-screen min-h-0 flex-col overflow-hidden bg-[radial-gradient(860px_420px_at_8%_-8%,rgba(43,127,255,0.12),transparent_58%),radial-gradient(760px_360px_at_92%_-14%,rgba(14,165,233,0.08),transparent_60%),linear-gradient(180deg,#F8FBFF_0%,#F6FAFF_46%,#F2F7FF_100%)] dark:bg-[radial-gradient(1100px_540px_at_16%_-14%,rgba(59,130,246,0.22),transparent_55%),radial-gradient(900px_420px_at_86%_-2%,rgba(56,189,248,0.12),transparent_60%),linear-gradient(180deg,#0A1224_0%,#0D1830_44%,#111E38_100%)]"
      >
        <div className="min-h-0 flex-1 overflow-y-auto p-4 lg:p-6">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="flex min-h-full flex-col"
            >
              <Outlet context={{ chats, createChat, sendMessage, loadingChatId } satisfies ChatLayoutContext} />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

export default ChatMain;
